import type { HTMLAttributes } from "react";

import { cn } from "@/lib/cn";

const tones = {
  neutral: "bg-surface-2 text-muted border-border",
  brand: "bg-brand-soft text-brand border-transparent",
  up: "bg-up-soft text-up border-transparent",
  down: "bg-down-soft text-down border-transparent",
  warn: "bg-warn-soft text-warn border-transparent",
  accent: "bg-accent-soft text-accent border-transparent",
  violet:
    "bg-tint-violet text-[#6d4fd8] border-transparent dark:text-[#b39dff]",
} as const;

export type BadgeTone = keyof typeof tones;

/**
 * A short label. `pill` is the rounded status chip (with `dot` for a leading
 * dot): "● Paid · to 23 Nov".
 */
export function Badge({
  tone = "neutral",
  pill = false,
  dot = false,
  className,
  children,
  ...props
}: HTMLAttributes<HTMLSpanElement> & {
  tone?: BadgeTone;
  pill?: boolean;
  dot?: boolean;
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 border font-semibold whitespace-nowrap",
        pill
          ? "rounded-full px-2.5 py-0.5 text-xs"
          : "rounded-md px-1.5 py-0.5 text-2xs",
        tones[tone],
        className,
      )}
      {...props}
    >
      {dot ? (
        <span aria-hidden className="size-1.5 rounded-full bg-current" />
      ) : null}
      {children}
    </span>
  );
}

/** ok / warn / fail / running → a badge tone. */
export function statusTone(status: string | null | undefined): BadgeTone {
  switch (status) {
    case "ok":
    case "loaded":
      return "up";
    case "warn":
    case "running":
      return "warn";
    case "fail":
    case "failed":
      return "down";
    default:
      return "neutral";
  }
}
